import { browser } from '$app/environment';
import { isEmpty } from 'lodash-es';
import { Mutex } from './mutex';
import { makeSpineAuthURL, gotoGameOverURL, toURL } from './url-utils';
import { TokenRefreshTickGenerator } from './tick-generator';
import { decodeAccessToken, type DecodedAccessToken } from './token-decoder';
import type { Bootstrap, SpineAuthUrlOptions, TokenSet } from './types';
import type { KeycloakClient } from './keycloak-client';
import { spineBus } from '$lib/services/gatekeeper/MessageBus';

export class SpineAuth {
    private tokenSet: TokenSet;
    private player?: Bootstrap['player'];
    private decoded: DecodedAccessToken;
    private errors: Bootstrap['errors'];
    private sessionEndedSubscription?: { unsubscribe: () => void };
    constructor(
        bootstrap: Bootstrap,
        private fetch: typeof global.fetch,
        private keycloak: KeycloakClient
    ) {
        this.tokenSet = bootstrap.tokenSet;
        this.player = bootstrap.player;
        this.errors = bootstrap.errors;
        this.decoded = decodeAccessToken(bootstrap.tokenSet.accessToken?.token);

        if (browser && this.errors.length) {
            window?.dataLayer.push({ event: 'fabric.login.fail', error_code: this.errors[0] });
            gotoGameOverURL(toURL(location));
        }

        if (browser && this.isLoggedIn()) {
            TokenRefreshTickGenerator.onTick(async () => {
                try {
                    await this.refreshTokens();
                } catch (error) {
                    console.error('Failed to refresh tokens', error);
                    gotoGameOverURL(toURL(location));
                }
            });
            this.subscribeToSessionEnded();
        }
    }
    private setTokenSet = (tokenSet: TokenSet) => {
        this.tokenSet = tokenSet;
        this.decoded = decodeAccessToken(tokenSet.accessToken?.token);
    };
    private async refreshTokens(originURL?: URL) {
        if (!this.tokenSet.refreshToken) {
            return;
        }
        return await Mutex.runExclusive('refreshTokens', async () => {
            const response = await this.fetch(
                makeSpineAuthURL(originURL ?? toURL(location), 'refresh')
            );
            if (!response.ok) {
                throw Error(`Failed to refresh tokens: ${response.status}:${response.statusText}`);
            }
            const tokenSet: TokenSet = await response.json();
            this.setTokenSet(tokenSet);
        });
    }
    private subscribeToSessionEnded() {
        this.sessionEndedSubscription = spineBus.subscribe(
            'external',
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (payload: Record<string, any>) => {
                if (payload.kind == 'session-ended') {
                    gotoGameOverURL(toURL(location));
                }
            }
        );
    }
    private unsubscribeFromSessionEnded = () => this.sessionEndedSubscription?.unsubscribe();
    /**
     * @param originURL The origin where this method was called from. Must be set if not called from the browser.
     * @returns This instance with refreshed tokens.
     */
    async refreshTokensIfNeeded(originURL?: URL) {
        const now = Date.now();
        const expiresAt = this.tokenSet.accessToken?.expiresAt;
        // The access token might have expired but it's possible we have a valid refresh token, so let's attempt the refresh.
        if (now >= (expiresAt ?? now)) {
            try {
                await this.refreshTokens(originURL);
            } catch (error) {
                console.error('Unable to refresh tokens', error);
                gotoGameOverURL(originURL ?? toURL(location));
            }
        }
        return this;
    }
    getTokenSet = () => this.tokenSet;
    logIn = (options?: SpineAuthUrlOptions) => {
        if (!browser) {
            throw Error('Only supported in the browser');
        }
        this.keycloak.signIn(options);
    };
    logOut = () => {
        if (!browser) {
            this.keycloak.signOutBackChannel(this.tokenSet.idToken?.token);
        } else {
            try {
                this.unsubscribeFromSessionEnded();
                this.keycloak.signOutFrontChannel(this.tokenSet.idToken?.token);
            } catch (error) {
                this.subscribeToSessionEnded();
            }
        }
    };
    isLoggedIn = () => !!this.tokenSet.accessToken && !isEmpty(this.getSessionId());
    getCasumoName = () => this.player?.casumoName;
    getName = () => this.player && `${this.player.firstName} ${this.player.lastName}`;
    getFirstName = () => this.player?.firstName;
    getLastName = () => this.player?.lastName;
    getSessionId = () => this.decoded?.sessionId;
    getPlayerId = () => this.decoded?.playerId;
}
